import type { CSSProperties, ReactNode } from 'react';

import { useReducedMotion } from '#hooks/useReducedMotion';

import { Gavi } from './Gavi';
import type { GaviEstado } from './Gavi';
import { claseBocadillo, claseBocWrap } from './gaviCss';

type BocadilloProps = {
  /** Lo que dice Gavi (ya traducido). Hasta 3 líneas a 360 px. */
  texto: string;
  /** Gesto de Gavi; por defecto, reposo. */
  estado?: GaviEstado;
  /** Cambia para repetir el gesto (ver `Gavi`). */
  clave?: string | number;
  /** Milisegundos del gesto (ver `Gavi`). */
  duracion?: number | null;
  /**
   * Debajo del texto: lema, botones… Los botones van dentro de un
   * `<div className="acciones">` si se quiere el aire de la maqueta (10 px).
   */
  children?: ReactNode;
  /**
   * `normal`: tarjeta con sombra (sobre una pantalla). `hoja`: sobre
   * surface-2 y sin sombra (dentro de una hoja). `astillero`: colores de la
   * escena de noche del Astillero.
   */
  variante?: 'normal' | 'hoja' | 'astillero';
  style?: CSSProperties;
  'data-testid'?: string;
};

/**
 * Gavi posada sobre su bocadillo (tarjeta con colita). El texto se anuncia
 * con `aria-live` y aparece con un fundido al cambiar.
 */
export function Bocadillo({
  texto,
  estado = 'reposo',
  clave,
  duracion,
  children,
  variante = 'normal',
  style,
  'data-testid': testId = 'bocadillo',
}: BocadilloProps) {
  const quieto = useReducedMotion();
  const clases = [claseBocadillo, 'bocadillo'];
  if (variante === 'hoja') {
    clases.push('en-hoja');
  } else if (variante === 'astillero') {
    clases.push('astillero');
  }
  return (
    <div className={claseBocWrap} style={style} data-testid={testId}>
      <span className="gavi-pos">
        <Gavi estado={estado} clave={clave} duracion={duracion} />
      </span>
      <div className={clases.join(' ')}>
        <span aria-live="polite" style={{ display: 'block' }}>
          <span
            // Nuevo texto → nuevo nodo: vuelve a entrar con el fundido.
            key={texto}
            className={quieto ? 'texto' : 'texto cambia'}
            data-testid="bocadillo-texto"
          >
            {texto}
          </span>
        </span>
        {children}
      </div>
    </div>
  );
}
